// Theme utility for dynamic theming
export interface ThemeConfig {
  name: string;
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  textLight: string;
}

export const themes: Record<string, ThemeConfig> = {
  default: {
    name: 'default',
    primary: '#ffffff',
    secondary: '#f5f5f5',
    accent: '#333333',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  },
  antares: {
    name: 'antares',
    primary: '#98281F',
    secondary: '#F5F5F5',
    accent: '#98281F',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  },
  nogales: {
    name: 'nogales',
    primary: '#717A00',
    secondary: '#F5F5F5',
    accent: '#717A00',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  },
  eonia: {
    name: 'eonia',
    primary: '#5C5EAA',
    secondary: '#F5F5F5',
    accent: '#5C5EAA',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  },
  azalea: {
    name: 'azalea',
    primary: '#AC3A5F',
    secondary: '#F5F5F5',
    accent: '#AC3A5F',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  },
  cayala: {
    name: 'cayala',
    primary: '#8B4513',
    secondary: '#F5F5F5',
    accent: '#8B4513',
    background: '#ffffff',
    text: '#333333',
    textLight: '#666666'
  }
};

export const setTheme = (themeName: string): void => {
  const theme = themes[themeName] || themes.default;
  
  // Set data-theme attribute on document element
  document.documentElement.setAttribute('data-theme', theme.name);
  
  // Update meta theme-color for browser UI
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme.primary);
  } else {
    // Create meta theme-color if it doesn't exist
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = theme.primary;
    document.head.appendChild(meta);
  }
  
  // Update manifest background color dynamically (for splash screen)
  const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
  if (manifestLink) {
    // Store original manifest URL if not already stored
    if (!manifestLink.dataset.originalHref) {
      manifestLink.dataset.originalHref = manifestLink.href;
    }
    
    // Create dynamic manifest
    const dynamicManifest = {
      "short_name": "Atrio",
      "name": "Atrio - Proyectos Inmobiliarios",
      "icons": [
        {
          "src": "logo-atrio.png",
          "sizes": "100x100",
          "type": "image/png"
        },
        {
          "src": "logo192.png",
          "type": "image/png",
          "sizes": "192x192"
        },
        {
          "src": "logo512.png",
          "type": "image/png",
          "sizes": "512x512"
        }
      ],
      "start_url": ".",
      "display": "standalone",
      "theme_color": theme.primary,
      "background_color": theme.background,
      "orientation": "portrait"
    };
    
    // Create blob URL for dynamic manifest
    const manifestBlob = new Blob([JSON.stringify(dynamicManifest)], {
      type: 'application/json'
    });
    const manifestUrl = URL.createObjectURL(manifestBlob);
    
    // Update manifest link
    manifestLink.href = manifestUrl;
  }
};

export const getThemeFromPath = (pathname: string): string => {
  // Extract project name from path
  const pathSegments = pathname.split('/').filter(segment => segment);
  
  if (pathSegments.length === 0 || pathSegments[0] === 'home') {
    return 'default';
  }
  
  if (pathSegments[0] === 'cayala') {
    return 'cayala';
  }
  
  if (pathSegments[0] === 'project' && pathSegments[1]) {
    const projectName = pathSegments[1];
    return themes[projectName] ? projectName : 'default';
  }
  
  return 'default';
};

export const resetTheme = (): void => {
  setTheme('default');
};
